import { Card, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable } from "utils/Functions";
import { post, delNoResponse } from "utils/WebService";
import PessoaCreateEdit from "./PessoaCreateEdit";
import ClientesSemCampanha from "./ClientesSemCampanha";
import MDSnackbar from "components/MDSnackbar";
import { ESCURO } from "assets/coresTema";

export default function PessoaConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        customFilters: {},
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", isSuccess: true });
    // Relê o aviso dos clientes sem campanha depois de incluir, alterar ou excluir um cliente
    const [versaoAviso, setVersaoAviso] = useState(0);

    const [filters, setFilters] = useState({
        nome: '',
        documento: '',
    });

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, customFilters }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            customFilters: customFilters ? customFilters : tableState.current.customFilters,
        };

        tableState.current = updatedTableState;

        post("/pessoa/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            nomeComum: updatedTableState.customFilters?.nomeComum,
            documento: updatedTableState.customFilters?.documento,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });

    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
        setVersaoAviso(v => v + 1);
    }

    const handleDelete = (id) => {
        if (window.confirm("Deseja realmente excluir este cadastro?")) {
            setLoadingTable(true);
            delNoResponse(`/pessoa/delete/${id}`)
                .then(() => {
                    setSnackbar({ open: true, message: "Cadastro excluído com sucesso!", isSuccess: true });
                    fetchData({ ...tableState });
                    setVersaoAviso(v => v + 1);
                })
                .catch(() => {
                    setSnackbar({ open: true, message: "Erro ao excluir cadastro.", isSuccess: false });
                    setLoadingTable(false);
                });
        }
    };

    const userColumns = [
        {
            header: "nome fantasia",
            accessorKey: "nomeComum",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "documento",
            accessorKey: "documento",
            cell: info => {
                let docFormatado = info.getValue() ? String(info.getValue()).replace(/\D/g, '') : "";
                if (docFormatado.length > 0) {
                    if (docFormatado.length <= 11) {
                        docFormatado = docFormatado.replace(/(\d{3})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d{1,2})/, '$1-$2');
                    } else {
                        docFormatado = docFormatado.replace(/(\d{2})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d)/, '$1.$2')
                                                   .replace(/(\d{3})(\d)/, '$1/$2')
                                                   .replace(/(\d{4})(\d{1,2})/, '$1-$2');
                    }
                }
                return <DefaultCell>{docFormatado}</DefaultCell>
            },
            isSorted: true
        },
        {
            // Carteira do cadastro do cliente (vazia = Global, todas as carteiras)
            header: "carteira",
            accessorKey: "nomeCarteira",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: false
        },
        {
            header: "ações",
            isSorted: false,
            cell: info => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton
                            onClick={() => handleDelete(info.row.original.id)}>
                            <MDTypography variant="h4" color="error">
                                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        const customFilters = {
            nomeComum: filters.nome,
            documento: filters.documento ? filters.documento.replace(/\D/g, '') : ''
        };
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, customFilters });
    };

    const handleCleanFilter = () => {
        setFilters({
            nome: '',
            documento: '',
        })
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], customFilters: {} });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <PessoaCreateEdit
                        id={selectedId}
                        closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                            <Grid item xs={12} sm={6}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Nome Fantasia</InputLabel>
                                <MDInput
                                    size="small"
                                    placeholder="Informe o nome fantasia..."
                                    value={filters.nome}
                                    onChange={({ target }) => setFilters({ ...filters, nome: target.value })}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item xs={12} sm={3}>
                                <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Documento</InputLabel>
                                <MDInput
                                    size="small"
                                    placeholder="Informe o documento..."
                                    value={filters.documento}
                                    onChange={({ target }) => {
                                        let val = target.value.replace(/\D/g, '');
                                        if (val.length <= 11) {
                                            val = val.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d{1,2})/, '$1-$2');
                                        } else {
                                            val = val.substring(0, 14);
                                            val = val.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})(\d)/, '$1/$2').replace(/(\d{4})(\d{1,2})/, '$1-$2');
                                        }
                                        setFilters({ ...filters, documento: val });
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleApplyFilter();
                                        }
                                    }}
                                    fullWidth
                                />
                            </Grid>

                             <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                                <MDBox>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={() => {
                                            setSelectedId(null);
                                            setIsCreateEdit(true);
                                        }}>
                                        Adicionar cliente
                                    </MDButton>
                                </MDBox>
                                <MDBox>
                                    <MDButton
                                        size="medium"
                                        sx={{ marginRight: 2 }}
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleCleanFilter}>
                                        Limpar filtro
                                    </MDButton>
                                    <MDButton
                                        size="medium"
                                        variant="contained"
                                        color="info"
                                        circular={false}
                                        iconOnly={false}
                                        onClick={handleApplyFilter}>
                                        Aplicar filtro
                                    </MDButton>
                                </MDBox>
                            </Grid>
                        </Grid>
                </Card>
                <ClientesSemCampanha
                    atualizar={versaoAviso}
                    onAbrirCliente={(idCliente) => {
                        setSelectedId(idCliente);
                        setIsCreateEdit(true);
                    }} />
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    changeFilter={aplicar}
                    tableState={tableState} />
            </Grid>
            {snackbar.open && (
                <MDSnackbar
                    color={snackbar.isSuccess ? "success" : "error"}
                    icon={snackbar.isSuccess ? "check" : "warning"}
                    title="Aviso"
                    content={snackbar.message}
                    dateTime=""
                    open={snackbar.open}
                    close={() => setSnackbar({ ...snackbar, open: false })}
                />
            )}
        </Grid>
    );
}
